import type { Route } from 'nextjs-routes';

type OGPageType = 'Homepage' | 'Root page' | 'Regular page';

const OG_TYPE_DICT: Record<Route['pathname'], OGPageType> = {
  '/': 'Homepage',

  // Transactions
  '/txs': 'Root page',
  '/pending-txs': 'Root page',
  '/internal-txs': 'Root page',
  '/txs/kettle/[hash]': 'Regular page',
  '/tx/[hash]': 'Regular page',

  // Blocks
  '/blocks': 'Root page',
  '/forked-blocks': 'Root page',
  '/uncle-blocks': 'Root page',
  '/block/[height_or_hash]': 'Regular page',
  '/block/countdown': 'Regular page',
  '/block/countdown/[height]': 'Regular page',

  // Accounts & Addresses
  '/accounts': 'Root page',
  '/accounts/label/[slug]': 'Root page',
  '/address/[hash]': 'Regular page',
  '/address/[hash]/contract-verification': 'Regular page',

  // Contracts
  '/verified-contracts': 'Root page',
  '/contract-verification': 'Root page',

  // Tokens
  '/tokens': 'Root page',
  '/token/[hash]': 'Regular page',
  '/token/[hash]/instance/[id]': 'Regular page',
  '/token-transfers': 'Root page',
  '/token-flow': 'Root page',

  // DApps
  '/apps': 'Root page',
  '/apps/[id]': 'Regular page',

  // Stats
  '/stats': 'Root page',
  '/stats/[id]': 'Regular page',
  '/charts-stats': 'Root page',
  '/top-statistics': 'Root page',
  '/leaderboard': 'Root page',

  // API
  '/api-docs': 'Regular page',
  '/graphiql': 'Regular page',
  '/api-plans': 'Root page',

  // Search
  '/search-results': 'Regular page',

  // User Account
  '/auth/profile': 'Regular page',
  '/auth/verify': 'Regular page',
  '/auth/verify-email-change': 'Regular page',
  '/account/overview': 'Regular page',
  '/account/settings': 'Regular page',
  '/account/profile': 'Regular page',
  '/account/merits': 'Regular page',
  '/account/watchlist': 'Regular page',
  '/account/api-key': 'Regular page',
  '/account/custom-abi': 'Regular page',
  '/account/tag-address': 'Regular page',
  '/account/name-tags': 'Regular page',
  '/account/txn-notes': 'Regular page',
  '/account/token-ignore': 'Regular page',
  '/account/api-dashboard': 'Regular page',
  '/account/advanced-filter': 'Regular page',
  '/account/verified-addresses': 'Root page',

  // Auth
  '/signin': 'Regular page',
  '/signup': 'Regular page',
  '/login': 'Regular page',
  '/forgot-password': 'Regular page',

  // Admin
  '/admin': 'Regular page',
  '/admin/users': 'Regular page',
  '/admin/tokens': 'Regular page',
  '/admin/contacts': 'Regular page',
  '/admin/contracts': 'Regular page',
  '/admin/settings': 'Regular page',
  '/admin/labels': 'Regular page',
  '/admin/name-tags': 'Regular page',
  '/admin/reports': 'Regular page',
  '/admin/approvals': 'Regular page',
  '/admin/api': 'Regular page',
  '/admin/ads': 'Regular page',
  '/admin/ad-submissions': 'Regular page',
  '/admin/node-management': 'Regular page',
  '/admin/user-activities': 'Regular page',

  // Public Tags
  '/public-tags/submit': 'Regular page',

  // L2/Rollup
  '/withdrawals': 'Root page',
  '/txn-withdrawals': 'Root page',
  '/deposits': 'Root page',
  '/output-roots': 'Root page',
  '/dispute-games': 'Root page',
  '/batches': 'Root page',
  '/batches/[number]': 'Regular page',
  '/batches/celestia/[height]/[commitment]': 'Regular page',

  // Blobs
  '/blobs': 'Root page',
  '/blobs/[hash]': 'Regular page',

  // AA & EIP-7702
  '/aa-transactions': 'Root page',
  '/eip7702-authorizations': 'Root page',

  // User Operations
  '/ops': 'Root page',
  '/op/[hash]': 'Regular page',

  // DEX
  '/pools': 'Root page',
  '/pools/[hash]': 'Regular page',

  // Name Domains
  '/name-domains': 'Root page',
  '/name-domains/[name]': 'Regular page',
  '/domain-lookup': 'Root page',
  '/name-lookup': 'Root page',

  // Validators
  '/validators': 'Root page',
  '/validators/[id]': 'Regular page',
  '/validatorset': 'Root page',
  '/validatorset/snapshot/[id]': 'Regular page',
  '/validatorsgroup': 'Root page',
  '/validatorsgroup/[groupName]': 'Regular page',

  // NFTs
  '/nft-top-contracts': 'Root page',
  '/nft-top-mints': 'Root page',
  '/nft-latest-mints': 'Root page',
  '/nft-trades': 'Root page',
  '/nft-transfers': 'Root page',

  // Gas
  '/gas-tracker': 'Root page',

  // Misc
  '/advanced-filter': 'Root page',
  '/csv-export': 'Regular page',
  '/visualize/sol2uml': 'Regular page',
  '/mud-worlds': 'Root page',
  '/interop-messages': 'Root page',
  '/nodetracker': 'Root page',
  '/labelcloud': 'Root page',
  '/code-reader': 'Root page',
  '/similar-contracts': 'Root page',
  '/contract-search': 'Root page',
  '/contract-diff': 'Root page',
  '/404': 'Regular page',

  // Tools
  '/tools/balance-checker': 'Regular page',
  '/tools/base64': 'Regular page',
  '/tools/block-date': 'Regular page',
  '/tools/broadcast-tx': 'Regular page',
  '/tools/csv-export': 'Regular page',
  '/tools/input-decoder': 'Regular page',
  '/tools/method-id': 'Regular page',
  '/tools/opcode': 'Regular page',
  '/tools/similar-contracts': 'Regular page',
  '/tools/token-approvals': 'Regular page',
  '/tools/token-standard': 'Regular page',
  '/tools/token-supply': 'Regular page',
  '/tools/tx-decoder': 'Regular page',
  '/tools/unit-converter': 'Regular page',
  '/tools/utf8': 'Regular page',
  '/tools/validate-onchain': 'Regular page',
  '/tools/verify-signature': 'Regular page',
  '/tools/vyper': 'Regular page',

  // Company/Info Pages
  '/about': 'Regular page',
  '/advertise': 'Regular page',
  '/brand-assets': 'Regular page',
  '/bug-bounty': 'Regular page',
  '/careers': 'Regular page',
  '/careers/[slug]': 'Regular page',
  '/contact': 'Regular page',
  '/priority-support': 'Regular page',
  '/privacy-policy': 'Regular page',
  '/terms': 'Regular page',

  // Service routes
  '/sprite': 'Regular page',
  '/api/metrics': 'Regular page',
  '/api/monitoring/invalid-api-schema': 'Regular page',
  '/api/log': 'Regular page',
  '/api/media-type': 'Regular page',
  '/api/proxy': 'Regular page',
  '/api/csrf': 'Regular page',
  '/api/healthz': 'Regular page',
  '/api/config': 'Regular page',
  '/api/auth/[...path]': 'Regular page',
  '/api/auth/profile/avatar': 'Regular page',
};

export default function getPageOgType(pathname: Route['pathname']) {
  return OG_TYPE_DICT[pathname] ?? 'Regular page';
}
